import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Library, Search, Printer, QrCode, Boxes, MapPin, Package, ExternalLink, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import PrintLabelDialog from '../components/PrintLabelDialog';

const FRONTEND_URL = window.location.origin;

// Normalises any entity to the shape PrintLabelDialog expects
const toLabelPayload = (kind, e) => {
  if (kind === 'item') {
    return {
      id: e.id,
      item_sku: e.item_sku,
      item_name: e.item_name,
      vendor_sku: e.vendor_sku,
      barcode_or_qr_value: e.barcode_or_qr_value || e.item_sku,
    };
  }
  if (kind === 'location') {
    return {
      id: e.id,
      item_sku: e.bin_id || e.id?.slice(0, 6),
      item_name: e.location_name || '',
      vendor_sku: '',
      barcode_or_qr_value: `${FRONTEND_URL}/labels/library?type=location&id=${e.id}`,
    };
  }
  if (kind === 'product') {
    return {
      id: e.id,
      item_sku: e.product_type_name || e.id?.slice(0, 6),
      item_name: e.description || '',
      vendor_sku: '',
      barcode_or_qr_value: `${FRONTEND_URL}/labels/library?type=product&id=${e.id}`,
    };
  }
  return e;
};

const LibraryCard = ({ entity, kind, selected, onToggle, onPrint, onOpen, highlight, refSetter }) => {
  const payload = toLabelPayload(kind, entity);
  const subtitle =
    kind === 'item' ? entity.item_category || ''
    : kind === 'location' ? entity.bin_id || ''
    : entity.description || '';

  return (
    <div
      ref={refSetter}
      className={`border rounded-sm p-3 bg-white flex gap-3 transition ${highlight ? 'border-orange-500 ring-2 ring-orange-300 shadow-md' : selected ? 'border-orange-500 ring-2 ring-orange-100' : 'border-slate-200 hover:border-slate-300'}`}
      data-testid={`library-card-${kind}-${entity.id}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="mt-1 cursor-pointer"
        data-testid={`library-select-${kind}-${entity.id}`}
      />
      <div className="bg-slate-50 border border-slate-100 p-1 shrink-0">
        <QRCodeSVG value={payload.barcode_or_qr_value} size={72} level="M" includeMargin={false} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-800 truncate" data-testid={`library-name-${kind}-${entity.id}`}>
          {payload.item_name || payload.item_sku}
        </div>
        <div className="text-xs mono text-slate-500 truncate">{payload.item_sku}</div>
        {subtitle && <div className="text-xs text-slate-400 truncate">{subtitle}</div>}
        <div className="flex gap-1 mt-2">
          <Button variant="outline" size="sm" onClick={onPrint} className="rounded-sm h-7 text-xs" data-testid={`library-print-${kind}-${entity.id}`}>
            <Printer className="w-3 h-3 mr-1" /> Print
          </Button>
          {onOpen && (
            <Button variant="ghost" size="sm" onClick={onOpen} className="rounded-sm h-7 text-xs" data-testid={`library-open-${kind}-${entity.id}`}>
              <ExternalLink className="w-3 h-3 mr-1" /> Open
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const LabelLibraryPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('items');
  const cardRefs = useRef({});
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({ items: new Set(), locations: new Set(), products: new Set() });
  const [printOpen, setPrintOpen] = useState(false);
  const [printPayload, setPrintPayload] = useState({ item: null, items: null, labelType: 'Item' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api().get('/items'),
      api().get('/locations'),
      api().get('/product-types'),
    ]).then(([i, l, p]) => {
      setItems(i.data || []);
      setLocations(l.data || []);
      setProductTypes(p.data || []);
    }).catch(() => toast.error('Failed to load library'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  // Honour ?type=&id= deep-links from scanned QRs — auto-switch tab and scroll to the card
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const t = searchParams.get('type');
    const id = searchParams.get('id');
    if (!t || !id || loading) return;
    const tabFor = t === 'location' ? 'locations' : t === 'product' ? 'products' : t === 'item' ? 'items' : null;
    if (!tabFor) return;
    setTab(tabFor);
    setHighlightId(id);
    setTimeout(() => {
      const el = cardRefs.current[`${t}-${id}`];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    searchParams.delete('type');
    searchParams.delete('id');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line
  }, [loading]);


  const filterFn = (e, fields) =>
    !search || fields.some(f => (e[f] || '').toString().toLowerCase().includes(search.toLowerCase()));

  const filteredItems = useMemo(
    () => items.filter(e => filterFn(e, ['item_sku', 'item_name', 'item_category', 'vendor_sku'])),
    [items, search]
  );
  const filteredLocations = useMemo(
    () => locations.filter(e => filterFn(e, ['location_name', 'bin_id'])),
    [locations, search]
  );
  const filteredProducts = useMemo(
    () => productTypes.filter(e => filterFn(e, ['product_type_name', 'description'])),
    [productTypes, search]
  );

  const toggleSelected = (bucket, id) => {
    setSelected(prev => {
      const next = new Set(prev[bucket]);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, [bucket]: next };
    });
  };

  const kindToLabelType = (kind) => kind === 'item' ? 'Item' : kind === 'location' ? 'Location' : kind === 'product' ? 'BOM' : 'Item';

  const printOne = (kind, entity) => {
    setPrintPayload({ item: toLabelPayload(kind, entity), items: null, labelType: kindToLabelType(kind) });
    setPrintOpen(true);
  };

  const printSelected = (kind, list, bucket) => {
    const ids = selected[bucket];
    const chosen = list.filter(e => ids.has(e.id));
    if (chosen.length === 0) { toast.error('Select at least one entry'); return; }
    setPrintPayload({ item: null, items: chosen.map(e => toLabelPayload(kind, e)), labelType: kindToLabelType(kind) });
    setPrintOpen(true);
  };

  const counts = {
    items: filteredItems.length,
    locations: filteredLocations.length,
    products: filteredProducts.length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-slideIn" data-testid="label-library-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Library className="w-6 h-6 text-orange-600" /> Label & QR Library
          </h1>
          <p className="text-slate-500 text-sm">Active library of every item, location and product in the shop. Browse, scan, print.</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/labels')} className="rounded-sm" data-testid="open-label-templates">
          <Tag className="w-4 h-4 mr-2" /> Label Templates
        </Button>
      </div>

      {/* Search */}
      <Card className="industrial-card">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search across items, locations, products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-sm"
              data-testid="library-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab} data-testid="library-tabs">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="items" data-testid="library-tab-items">
            <Boxes className="w-4 h-4 mr-1" /> Items <span className="ml-1.5 text-xs mono text-slate-500">{counts.items}</span>
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="library-tab-locations">
            <MapPin className="w-4 h-4 mr-1" /> Locations <span className="ml-1.5 text-xs mono text-slate-500">{counts.locations}</span>
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="library-tab-products">
            <Package className="w-4 h-4 mr-1" /> Products <span className="ml-1.5 text-xs mono text-slate-500">{counts.products}</span>
          </TabsTrigger>
        </TabsList>

        {/* ITEMS */}
        <TabsContent value="items" className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500" data-testid="library-items-selected-count">
              {selected.items.size} selected
            </div>
            {selected.items.size > 0 && (
              <Button onClick={() => printSelected('item', items, 'items')} className="bg-orange-600 hover:bg-orange-700 rounded-sm" data-testid="library-bulk-print-items">
                <Printer className="w-4 h-4 mr-2" /> Print {selected.items.size} item label{selected.items.size === 1 ? '' : 's'}
              </Button>
            )}
          </div>
          {filteredItems.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="library-items-empty">
              No items match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredItems.map(e => (
                <LibraryCard
                  key={e.id}
                  refSetter={(el) => { if (el) cardRefs.current[`item-${e.id}`] = el; }}
                  entity={e}
                  kind="item"
                  selected={selected.items.has(e.id)}
                  highlight={highlightId === e.id}
                  onToggle={() => toggleSelected('items', e.id)}
                  onPrint={() => printOne('item', e)}
                  onOpen={() => navigate(`/items/view/${e.id}`)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* LOCATIONS */}
        <TabsContent value="locations" className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500" data-testid="library-locations-selected-count">
              {selected.locations.size} selected
            </div>
            {selected.locations.size > 0 && (
              <Button onClick={() => printSelected('location', locations, 'locations')} className="bg-orange-600 hover:bg-orange-700 rounded-sm" data-testid="library-bulk-print-locations">
                <Printer className="w-4 h-4 mr-2" /> Print {selected.locations.size} location label{selected.locations.size === 1 ? '' : 's'}
              </Button>
            )}
          </div>
          {filteredLocations.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="library-locations-empty">
              No locations yet. Add a location under Inventory → Locations.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredLocations.map(e => (
                <LibraryCard
                  key={e.id}
                  refSetter={(el) => { if (el) cardRefs.current[`location-${e.id}`] = el; }}
                  entity={e}
                  kind="location"
                  selected={selected.locations.has(e.id)}
                  highlight={highlightId === e.id}
                  onToggle={() => toggleSelected('locations', e.id)}
                  onPrint={() => printOne('location', e)}
                  onOpen={() => navigate('/locations')}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products" className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500" data-testid="library-products-selected-count">
              {selected.products.size} selected
            </div>
            {selected.products.size > 0 && (
              <Button onClick={() => printSelected('product', productTypes, 'products')} className="bg-orange-600 hover:bg-orange-700 rounded-sm" data-testid="library-bulk-print-products">
                <Printer className="w-4 h-4 mr-2" /> Print {selected.products.size} product label{selected.products.size === 1 ? '' : 's'}
              </Button>
            )}
          </div>
          {filteredProducts.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="library-products-empty">
              No product types yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredProducts.map(e => (
                <LibraryCard
                  key={e.id}
                  refSetter={(el) => { if (el) cardRefs.current[`product-${e.id}`] = el; }}
                  entity={e}
                  kind="product"
                  selected={selected.products.has(e.id)}
                  highlight={highlightId === e.id}
                  onToggle={() => toggleSelected('products', e.id)}
                  onPrint={() => printOne('product', e)}
                  onOpen={() => navigate('/product-types')}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <PrintLabelDialog
        open={printOpen}
        onOpenChange={(open) => {
          setPrintOpen(open);
          if (!open) setPrintPayload({ item: null, items: null, labelType: 'Item' });
        }}
        item={printPayload.item}
        items={printPayload.items}
        labelType={printPayload.labelType}
      />
    </div>
  );
};

export default LabelLibraryPage;
